import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, useMediaQuery, IconButton, Drawer, List, ListItem, ListItemText, Collapse } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Logo from './Logo';
import ServicesDropdown from './ServicesDropdown';

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Services'},
  // { label: 'Portfolio', to: '/portfolio' },
  { label: 'Technology', to: '/technology' },
  // { label: 'Blog', to: '/blog' },
  { label: 'Contact', to: '/contact' },
];

const serviceItems = [
  { label: 'Software Development', to: '/services/software-development' },
  { label: 'Mobile Apps', to: '/services/mobile-apps' },
  { label: 'Web Development', to: '/services/web-development' },
  { label: 'AI & Automation', to: '/services/ai-automation' },
  { label: 'Cloud Solutions', to: '/services/cloud-solutions' },
  { label: 'UI/UX Design', to: '/services/ui-ux-design' },
];

const Navbar = () => {
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:900px)');
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const handleServicesMouseEnter = () => {
    setIsServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    setIsServicesOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleMobileServices = () => {
    setMobileServicesOpen(!mobileServicesOpen);
  };

  const MobileDrawer = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={toggleMobileMenu}
      PaperProps={{
        sx: {
          width: '75%',
          maxWidth: '320px',
          background: 'rgba(249, 247, 247, 0.98)',
          backdropFilter: 'blur(12px)',
          pt: 2,
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 2 }}>
        <IconButton onClick={toggleMobileMenu}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navLinks.map((link) => {
          const isActive = location.pathname === link.to;
          const isServices = link.label === 'Services';

          if (isServices) {
            return (
              <React.Fragment key={link.label}>
                <ListItem
                  button
                  onClick={toggleMobileServices}
                  sx={{
                    color: '#112D4E',
                    fontWeight: 600,
                    py: 2,
                    '&:hover': {
                      color: '#3F72AF',
                      background: 'rgba(63, 114, 175, 0.08)',
                    },
                  }}
                >
                  <ListItemText 
                    primary={link.label}
                    primaryTypographyProps={{
                      fontSize: 18,
                      fontWeight: 'inherit',
                    }}
                  />
                  {mobileServicesOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={mobileServicesOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {serviceItems.map((service) => (
                      <ListItem
                        key={service.label}
                        component={RouterLink}
                        to={service.to}
                        onClick={toggleMobileMenu}
                        sx={{
                          pl: 4,
                          color: location.pathname === service.to ? '#3F72AF' : '#112D4E',
                          fontWeight: location.pathname === service.to ? 800 : 500,
                          py: 1.5,
                          '&:hover': {
                            color: '#3F72AF',
                            background: 'rgba(63, 114, 175, 0.08)',
                          },
                        }}
                      >
                        <ListItemText 
                          primary={service.label}
                          primaryTypographyProps={{
                            fontSize: 16,
                            fontWeight: 'inherit',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <ListItem
              key={link.label}
              component={RouterLink}
              to={link.to}
              onClick={toggleMobileMenu}
              sx={{
                color: isActive ? '#3F72AF' : '#112D4E',
                fontWeight: isActive ? 800 : 600,
                py: 2,
                '&:hover': {
                  color: '#3F72AF',
                  background: 'rgba(63, 114, 175, 0.08)',
                },
              }}
            >
              <ListItemText 
                primary={link.label}
                primaryTypographyProps={{
                  fontSize: 18,
                  fontWeight: 'inherit',
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: 'rgba(249, 247, 247, 0.85)',
        backdropFilter: 'blur(12px)',
        borderRadius: { xs: 0, md: 4 },
        // boxShadow: '0 8px 32px rgba(63,114,175,0.10)',
        color: '#112D4E',
        border: '1.5px solid rgba(219, 226, 239, 0.3)',
        mt: { xs: 0 },
        py: { xs: 0, md: 0.5 },
        maxWidth: { xs: '100%', md: '1400px' },
        mx: 'auto',
        left: { xs: 0, md: '50%' },
        transform: { xs: 'none', md: 'translateX(-50%)' },
        zIndex: 1100,
      }}
    >
      <Toolbar sx={{ 
        justifyContent: 'space-between', 
        minHeight: { xs: 56, md: 72 },
        py: { xs: 0.5, md: 1 }
      }}>
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          component={RouterLink}
          to="/"
          sx={{
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'transform 0.18s',
            '&:hover': {
              transform: 'scale(1.06)',
            },
          }}
        >
          <Logo size={isMobile ? 32 : 40} />
          <Typography
            variant="h5"
            sx={{
              ml: 1,
              fontWeight: 'bold',
              color: '#3F72AF',
              letterSpacing: 2,
              fontFamily: 'Segoe UI, Arial, sans-serif',
              userSelect: 'none',
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            Virves
          </Typography>
        </Box>
        
        {isMobile ? (
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={toggleMobileMenu}
            sx={{ ml: 2 }}
          >
            <MenuIcon />
          </IconButton>
        ) : (
          <Box display="flex" alignItems="center" gap={1.5}>
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              const isServices = link.label === 'Services';

              return (
                <Box
                  key={link.label}
                  position="relative"
                  onMouseEnter={isServices ? handleServicesMouseEnter : undefined}
                  onMouseLeave={isServices ? handleServicesMouseLeave : undefined}
                >
                  <Button
                    component={link.to ? RouterLink : 'button'}
                    to={link.to}
                    disableRipple
                    sx={{
                      position: 'relative',
                      color: isActive ? '#3F72AF' : '#112D4E',
                      fontWeight: isActive ? 800 : 600,
                      borderRadius: 2,
                      px: 2.5,
                      py: 1.2,
                      mx: 0.5,
                      textTransform: 'none',
                      fontSize: 17,
                      letterSpacing: 1,
                      overflow: 'hidden',
                      transition: 'all 0.2s',
                      '&:hover': {
                        color: '#3F72AF',
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                  {isServices && <ServicesDropdown isOpen={isServicesOpen} />}
                </Box>
              );
            })}
          </Box>
        )}
        <MobileDrawer />
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
